interface AttributionData {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    term?: string;
    gclid?: string; // Google Ads click ID
    fbclid?: string; // Facebook click ID
    ttclid?: string; // TikTok click ID
    twclid?: string; // Twitter click ID
    msclkid?: string; // Microsoft Ads click ID
    referrer?: string;
    timestamp: number;
    isRewardful: boolean;
}

export class AttributionHelper {
    private static readonly ATTRIBUTION_KEY = "klap_attribution";
    private static readonly ATTRIBUTION_EXPIRY_DAYS = 30; // Attribution window in days

    /**
     * Captures attribution data from URL parameters and stores it
     */
    static captureAttribution(url?: string): AttributionData | null {
        if (typeof window === "undefined") return null;

        try {
            const urlToParse = url || window.location.href;
            const urlObj = new URL(urlToParse);
            const params = urlObj.searchParams;

            // Extract UTM parameters
            const source = params.get("utm_source");
            const medium = params.get("utm_medium");
            const campaign = params.get("utm_campaign");
            const content = params.get("utm_content");
            const term = params.get("utm_term");

            // Extract platform-specific click IDs
            const gclid = params.get("gclid");
            const fbclid = params.get("fbclid");
            const ttclid = params.get("ttclid");
            const twclid = params.get("twclid");
            const msclkid = params.get("msclkid");

            // Get referrer
            const referrer = document.referrer;

            // Check if it's a Rewardful referral
            const isRewardful = !!(window as any).Rewardful && !!(window as any).Rewardful.referral;

            // Only store attribution if we have meaningful data
            const hasAttributionData = source || medium || campaign || gclid || fbclid || ttclid || twclid || msclkid || isRewardful;

            if (!hasAttributionData) return null;

            const attributionData: AttributionData = {
                ...(source && { source }),
                ...(medium && { medium }),
                ...(campaign && { campaign }),
                ...(content && { content }),
                ...(term && { term }),
                ...(gclid && { gclid }),
                ...(fbclid && { fbclid }),
                ...(ttclid && { ttclid }),
                ...(twclid && { twclid }),
                ...(msclkid && { msclkid }),
                ...(referrer && { referrer }),
                timestamp: Date.now(),
                isRewardful,
            };

            // Store the attribution data (first-click attribution)
            const existingAttribution = this.getStoredAttribution();
            if (!existingAttribution || this.isAttributionExpired(existingAttribution)) {
                localStorage.setItem(this.ATTRIBUTION_KEY, JSON.stringify(attributionData));
            }

            return attributionData;
        } catch (error) {
            console.error("Error capturing attribution:", error);
            return null;
        }
    }

    /**
     * Gets the stored attribution data
     */
    static getStoredAttribution(): AttributionData | null {
        if (typeof window === "undefined") return null;

        try {
            const stored = localStorage.getItem(this.ATTRIBUTION_KEY);
            if (!stored) return null;

            const attributionData = JSON.parse(stored) as AttributionData;

            // Check if attribution has expired
            if (this.isAttributionExpired(attributionData)) {
                this.clearAttribution();
                return null;
            }

            return attributionData;
        } catch (error) {
            console.error("Error getting stored attribution:", error);
            return null;
        }
    }

    /**
     * Gets attribution source for analytics
     */
    static getAttributionSource(): string {
        const attribution = this.getStoredAttribution();
        if (!attribution) return "direct";

        // Priority order for determining source
        if (attribution.isRewardful) return "rewardful";
        if (attribution.gclid || attribution.source === "google") return "google_ads";
        if (attribution.fbclid || attribution.source === "facebook") return "facebook_ads";
        if (attribution.ttclid || attribution.source === "tiktok") return "tiktok_ads";
        if (attribution.twclid || attribution.source === "twitter") return "twitter_ads";
        if (attribution.msclkid || attribution.source === "bing") return "microsoft_ads";
        if (attribution.source) return attribution.source;
        if (attribution.referrer) {
            // Extract domain from referrer
            try {
                const referrerDomain = new URL(attribution.referrer).hostname;
                return `referrer_${referrerDomain.replace(/^www\./, "")}`;
            } catch {
                return "referrer";
            }
        }

        return "direct";
    }

    /**
     * Gets full attribution data for analytics
     */
    static getAttributionForAnalytics() {
        const attribution = this.getStoredAttribution();
        const source = this.getAttributionSource();

        return {
            attribution_source: source,
            attribution_medium: attribution?.medium || null,
            attribution_campaign: attribution?.campaign || null,
            attribution_content: attribution?.content || null,
            attribution_term: attribution?.term || null,
            attribution_timestamp: attribution?.timestamp || null,
            attribution_referrer: attribution?.referrer || null,
            attribution_is_rewardful: attribution?.isRewardful || false,
            // Include click IDs for debugging
            attribution_gclid: attribution?.gclid || null,
            attribution_fbclid: attribution?.fbclid || null,
            attribution_ttclid: attribution?.ttclid || null,
        };
    }

    /**
     * Clears stored attribution data
     */
    static clearAttribution(): void {
        if (typeof window === "undefined") return;
        localStorage.removeItem(this.ATTRIBUTION_KEY);
    }

    /**
     * Checks if attribution data has expired
     */
    private static isAttributionExpired(attribution: AttributionData): boolean {
        const expiryTime = attribution.timestamp + (this.ATTRIBUTION_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
        return Date.now() > expiryTime;
    }

    /**
     * Forces update of attribution (for testing or last-click attribution)
     */
    static updateAttribution(url?: string): AttributionData | null {
        // Clear existing attribution first
        this.clearAttribution();
        return this.captureAttribution(url);
    }
} 